import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Gauge, TrendingUp, Layers, Boxes } from "lucide-react";
import { BRAND, DOMAINS, IMPACT_METRICS } from "@/content";
import { useEffect, useState } from "react";
import axios from "axios";
import { LogoLabelled } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&w=1600&q=80";

const iconFor = {
  fcf: Gauge, sales: TrendingUp, supply: Layers, mfg: Boxes,
};

export default function HomePage() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-white">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <span className="eyebrow reveal reveal-1">Boutique Advisory · Industrial Manufacturing</span>
            <h1 className="mt-5 text-[44px] sm:text-[56px] lg:text-[72px] leading-[0.98] tracking-[-0.035em] font-semibold text-slate-900 reveal reveal-2">
              We turn <span className="italic font-normal text-[#712ae2]">operational drag</span><br />
              into <span className="text-slate-900">free cash flow.</span>
            </h1>
            <p className="mt-6 max-w-xl text-[17px] leading-[1.65] text-slate-600 reveal reveal-3">
              Traditional consulting diagnoses working-capital lockups in weeks. Indusynq isolates the one system constraint,
              quantifies its cash-flow cost, and hands you a 30-90 day plan you can start on Monday.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 reveal reveal-4">
              <Link
                to="/diagnostic"
                data-testid="hero-diagnostic-cta"
                className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-sm font-medium px-5 py-3 hover:bg-black transition-colors"
              >
                Run the Constraint Diagnostic <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-cta"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-900 text-sm font-medium px-5 py-3 hover:bg-slate-50 transition-colors"
              >
                Explore Services <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-8 max-w-xl">
              <StatBlock k={stats?.capital_released_inr_cr ?? 850} suffix=" Cr+" label="Working capital released" />
              <StatBlock k={stats?.otif_avg_uplift_pct ?? 46} suffix="%" label="Avg. OTIF uplift" />
              <StatBlock k={BRAND.years} suffix=" yrs" label="Industrial P&L experience" isString />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden bg-slate-900 aspect-[4/5] shadow-[0_30px_80px_-30px_rgba(15,23,42,0.4)]">
              <img src={HERO_IMG} alt="Modern manufacturing floor" className="absolute inset-0 h-full w-full object-cover opacity-90" />
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900/60 via-slate-900/10 to-transparent" />
              <div className="absolute left-6 right-6 top-6 flex items-center justify-between text-[11px] tracking-[0.22em] uppercase text-white/80">
                <span>Constraint Map</span>
                <span className="font-mono-tabular">Live</span>
              </div>
              <div className="absolute left-6 right-6 bottom-6 rounded-xl border border-white/15 bg-white/10 backdrop-blur-md p-5 text-white">
                <div className="text-[11px] tracking-[0.22em] uppercase text-white/70">Cash Conversion Cycle</div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="number-hero text-4xl">92</span>
                  <span className="text-white/70 text-sm">days · benchmark 45</span>
                </div>
                <div className="mt-4 h-1.5 bg-white/15 rounded-full overflow-hidden">
                  <div className="h-full w-[62%] bg-[#712ae2]" />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-[11px]">
                  <MiniStat k="DSO" v="64" />
                  <MiniStat k="Inv" v="58" />
                  <MiniStat k="DPO" v="30" />
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden lg:block rounded-xl bg-white border border-slate-200 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.25)] px-5 py-4 w-[240px]">
              <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500">Working Cap Release</div>
              <div className="mt-1 number-hero text-2xl text-slate-900">₹18.4 Cr</div>
              <div className="text-xs text-emerald-600 font-medium mt-1">↑ 30–55% of gap · 90 days</div>
            </div>
          </div>
        </div>

        <TickerBar />
      </section>

      {/* SIX PILLARS — hexagon brand mark */}
      <section className="mx-auto max-w-[1440px] px-6 lg:px-10 py-24" data-testid="six-pillars">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="eyebrow">The Indusynq Hexagon</span>
            <h2 className="mt-4 text-3xl lg:text-5xl tracking-tight font-semibold text-slate-900 leading-[1.05]">
              Six pillars. <span className="text-slate-500">One synchronised system.</span>
            </h2>
            <p className="mt-6 text-[17px] text-slate-600 leading-[1.7] max-w-xl">
              Indusynq treats an industrial P&L as a single connected mesh — not as siloed functions. Each vertex of the hexagon
              is a lever we tune in concert with the others.
            </p>
            <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4 text-[15px]">
              {[
                { k: "Sales", v: "Deal quality, margin health" },
                { k: "Supply Chain", v: "OTIF, resilience, dual-source" },
                { k: "Manufacturing", v: "OEE, constraint discipline" },
                { k: "Cash", v: "CCC, working-capital velocity" },
                { k: "AI Tech", v: "Industry 4.0, digital ops" },
                { k: "People", v: "Standard work, capability" },
              ].map((p, i) => (
                <li key={i} data-testid={`pillar-${p.k.toLowerCase().replace(/\s+/g, '-')}`} className="flex items-start gap-3">
                  <span className="mt-1.5 h-2 w-2 rounded-sm bg-gradient-to-br from-[#7C3AED] to-[#0EA5E9] shrink-0" />
                  <div>
                    <div className="font-semibold text-slate-900">{p.k}</div>
                    <div className="text-slate-500 text-[13.5px]">{p.v}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7 relative flex items-center justify-center">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="h-[440px] w-[440px] max-w-full rounded-full bg-gradient-to-br from-[#7C3AED]/8 via-transparent to-[#0EA5E9]/8 blur-2xl" />
            </div>
            <LogoLabelled size={560} className="relative max-w-full h-auto" />
          </div>
        </div>
      </section>

      {/* PRINCIPLE STRIP */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-1">
            <span className="eyebrow">The Operating Principle</span>
            <h2 className="mt-3 text-2xl lg:text-3xl tracking-tight font-semibold text-slate-900">
              Improving a non-constraint is a decorated waste of cash.
            </h2>
          </div>
          <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-6 text-[15px] text-slate-600 leading-relaxed">
            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500 mb-2">Constraint First</div>
              We locate the one bottleneck governing total throughput — machine, process, skill, policy, or supplier.
            </div>
            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500 mb-2">Financial Quantification</div>
              Every operational metric is translated into rupees of released cash, avoided cost, or protected margin.
            </div>
            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500 mb-2">Trade-off Disclosure</div>
              We surface the tension in every recommendation. No consulting slide is complete without its counter-cost.
            </div>
          </div>
        </div>
      </section>

      {/* DOMAINS */}
      <section className="mx-auto max-w-[1440px] px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 items-end">
          <div className="lg:col-span-6">
            <span className="eyebrow">Four Interconnected Domains</span>
            <h2 className="mt-4 text-3xl lg:text-5xl tracking-tight font-semibold text-slate-900">
              Cash flow is the North Star.<br />
              <span className="text-slate-500">Sales, supply, and manufacturing are its tributaries.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 flex lg:justify-end">
            <Link to="/services" data-testid="domains-explore-link" className="inline-flex items-center gap-2 text-sm font-medium text-slate-900 hover:gap-3 transition-all">
              Detailed service breakdown <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {DOMAINS.map((d) => {
            const Icon = iconFor[d.key];
            return (
              <div
                key={d.key}
                data-testid={`domain-card-${d.key}`}
                className="ind-card p-7 group"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500">{d.number} · {d.kicker}</div>
                    <h3 className="mt-2 text-xl lg:text-2xl font-semibold tracking-tight text-slate-900">{d.title}</h3>
                  </div>
                  <div className="h-11 w-11 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
                <ul className="mt-6 space-y-3 text-[15px] text-slate-600">
                  {d.bullets.map((b, i) => (
                    <li key={i} className="flex gap-3">
                      <span className={`mt-2 h-1.5 w-1.5 rounded-full shrink-0 ${d.dot}`} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* IMPACT METRICS */}
      <section className="bg-[#0F172A] text-white">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 py-20">
          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-[11px] tracking-[0.22em] uppercase text-slate-400">Track Record</span>
              <h2 className="mt-3 text-3xl lg:text-5xl tracking-tight font-semibold">
                Numbers, not narratives.
              </h2>
            </div>
            <Link to="/case-studies" data-testid="impact-cta" className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 text-sm font-semibold px-5 py-3">
              Read the case studies <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-10">
            {IMPACT_METRICS.map((m, i) => (
              <div key={i} data-testid={`impact-metric-${i}`} className="border-l-2 border-[#712ae2] pl-5">
                <div className="number-hero text-3xl lg:text-4xl tracking-tight">{m.value}</div>
                <div className="text-sm text-slate-300 mt-2 leading-snug">{m.label}</div>
                <div className="text-xs text-slate-500 mt-1">{m.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER SNAPSHOT */}
      <section className="mx-auto max-w-[1440px] px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="relative rounded-2xl overflow-hidden aspect-[4/5] bg-slate-100">
            <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_constraint-to-cash/artifacts/jglpgnyy_IMG_4695.jpeg" alt="Founder" className="absolute inset-0 h-full w-full object-cover object-top" />
            <div className="absolute left-5 bottom-5 rounded-lg bg-white/95 backdrop-blur px-4 py-3 border border-slate-200">
              <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500">Founder</div>
              <div className="text-base font-semibold text-slate-900">{BRAND.founder}</div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7">
          <span className="eyebrow">Peer-Level, Candid, Action-Oriented</span>
          <h2 className="mt-4 text-3xl lg:text-5xl tracking-tight font-semibold text-slate-900">
            30 years on the plant floor. And in the boardroom.
          </h2>
          <p className="mt-6 text-[17px] text-slate-600 leading-[1.7] max-w-2xl">
            {BRAND.founder} founded Indusynq after three decades running P&Ls of ₹450 Cr+ in Metals, Power Transmission,
            and Industrial Distribution — including Senior Vice President roles at Primetals Technologies (Mitsubishi Heavy
            Industries Group), leadership at Timken India, and VP tenure at Moglix during its Unicorn ascent.
          </p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { k: "₹1,700 Cr", v: "Throughput managed" },
              { k: "8+ yrs", v: "P&L ownership" },
              { k: "14 sites", v: "Multi-plant delivery" },
              { k: "MBA · ISB", v: "Strategy + Digital Transform." },
            ].map((s, i) => (
              <div key={i}>
                <div className="number-hero text-xl text-slate-900">{s.k}</div>
                <div className="text-xs text-slate-500 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex gap-3">
            <Link to="/about" data-testid="founder-about-cta" className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-sm font-medium px-5 py-3">
              Full biography <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" data-testid="founder-contact-cta" className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-900 text-sm font-medium px-5 py-3 hover:bg-slate-50">
              Talk to Vivek
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1440px] px-6 lg:px-10 pb-24">
        <div className="rounded-2xl bg-gradient-to-br from-[#0F172A] via-[#111827] to-[#1E1B4B] text-white p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#712ae2]/30 blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <span className="text-[11px] tracking-[0.22em] uppercase text-slate-400">Two hours. One constraint.</span>
              <h2 className="mt-3 text-3xl lg:text-5xl font-semibold tracking-tight max-w-2xl">
                Bring your baseline. Leave with a quantified plan.
              </h2>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link to="/diagnostic" data-testid="footer-cta-diagnostic" className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 text-sm font-semibold px-6 py-3">
                Start the diagnostic <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatBlock({ k, suffix = "", label, isString = false }) {
  return (
    <div>
      <div className="number-hero text-2xl lg:text-3xl text-slate-900">
        {isString ? k : `₹${k}`.replace(/^₹(\d+)/, (m, n) => (label.includes("Cr") || suffix.includes("Cr") ? `₹${n}` : n))}
        <span className="text-slate-500 font-normal">{suffix}</span>
      </div>
      <div className="text-xs text-slate-500 mt-1 leading-snug">{label}</div>
    </div>
  );
}

function MiniStat({ k, v }) {
  return (
    <div className="rounded-md bg-white/5 border border-white/10 px-2 py-1.5">
      <div className="text-white/50 text-[10px] tracking-[0.18em] uppercase">{k}</div>
      <div className="font-mono-tabular text-white text-sm">{v}d</div>
    </div>
  );
}

function TickerBar() {
  const items = [
    "Cash Conversion Cycle", "OEE at the Constraint", "OTIF Recovery", "Dual-Source Strategy",
    "Working Capital Release", "Drum-Buffer-Rope", "Contribution Margin", "Supplier Concentration",
  ];
  const list = [...items, ...items];
  return (
    <div className="border-t border-slate-200 bg-white overflow-hidden">
      <div className="ticker-track whitespace-nowrap py-4 flex gap-10 text-[11px] tracking-[0.22em] uppercase text-slate-400">
        {list.map((t, i) => (
          <span key={i} className="flex items-center gap-4">
            <span>{t}</span>
            <span className="text-slate-300">·</span>
          </span>
        ))}
      </div>
    </div>
  );
}
